import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  HeartPulse,
  Users,
  ChevronDown,
  Loader2,
  User,
  Clock,
  UserCheck,
  CalendarDays,
  Home,
  Laptop,
  Monitor,
  Briefcase,
  MapPin,
  Navigation,
  History,
  FileCheck,
  Receipt,
  Network,
  CalendarOff,
  UserPlus,
  FileText,
  GitBranch,
  GraduationCap,
  Layers,
  Award,
  CalendarRange,
  DollarSign,
  Calendar,
  Bell,
  UserMinus,
  // Badge,
  MonitorCog,
  // LocationEdit,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { RoutesLink } from "../../utility/RouteLinks";
import { permissionKeys } from "@/utility/helper";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { openTermsModal } from "@/store/feature/UserSlice";
import { showToastNotification } from "@/utility/helper";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface SidebarProps {
  isCollapsed: boolean;
  toggleSidebar: () => void;
  isMobileOpen: boolean;
  toggleMobileSidebar: () => void;
}

const SidebarItemTooltip = ({
  children,
  label,
  enabled,
}: {
  children: React.ReactNode;
  label: string;
  enabled: boolean;
}) => {
  if (!enabled) return <>{children}</>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        side="right"
        className="bg-[#0B1121] text-white border border-white/10 px-3 py-1.5 rounded-lg shadow-xl font-medium tracking-wide text-xs z-100"
        sideOffset={10}
      >
        {label}
      </TooltipContent>
    </Tooltip>
  );
};

let globalSidebarScrollTop = 0;

interface NavItem {
  label: string;
  tooltip?: string;
  Icon: LucideIcon;
  path?: string;
  relatedRoutes?: string[];
  roles?: string[];
  accessKey?: any;
  children?: NavItem[];
}


const LockedBadge = () => (
  <span className="ml-auto shrink-0 text-[10px] font-semibold text-amber-400/90 bg-amber-500/10 px-1.5 py-0.5 rounded-full border border-amber-500/20">
    Locked
  </span>
);

const Sidebar: React.FC<SidebarProps> = ({
  isCollapsed,
  toggleSidebar,
  isMobileOpen,
  toggleMobileSidebar,
}) => {
  const location = useLocation();
  // const navigate = useNavigate();
  const dispatch = useDispatch();
  //   const { user: userDetails, userDetailsLoading } = useSelector(
  //     (state: RootState) => state.user,
  //   );
  const userDetails = useSelector((state: RootState) => state.user.user);
  const userDetailsLoading = useSelector(
    (state: RootState) => state.user.userDetailsLoading,
  );

  const isUserDataReady =
    !!userDetails &&
    userDetailsLoading !== "pending" &&
    userDetailsLoading !== "idle";

  const isTandcAccepted = isUserDataReady
    ? Boolean(userDetails?.tandcAccepted)
    : true;


  const handleGuardedNavClick = (e: React.MouseEvent) => {
    if (!isTandcAccepted) {
      e.preventDefault();
      e.stopPropagation();
      showToastNotification("danger", "Please accept the Terms & Conditions to continue.");
      dispatch(openTermsModal());
    }
  };

  const navRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      if (node !== null) {
        node.scrollTop = globalSidebarScrollTop;
      }
    },
    [userDetails],
  );

  const checkAccess = (accessKey?: any) => {
    if (!accessKey) return true;

    const userLevel = userDetails?.designation?.level;

    // Condition 1: Level 0 or 1 gets full access to everything
    if (
      userLevel !== undefined &&
      userLevel !== null &&
      (Number(userLevel) === 0 || Number(userLevel) === 1)
    ) {
      return true;
    }

    // Condition 2: Other levels check level match first
    if (userLevel !== undefined && userLevel !== null) {
      if (Array.isArray(userLevel)) {
        if (
          userLevel.some((level: any) => String(level) === String(accessKey))
        ) {
          return true;
        }
      } else if (String(userLevel) === String(accessKey)) {
        return true;
      }
    }

    // Check fallback to menuBar
    const menuBar = userDetails?.designation?.menuBar?.keyNames;
    const result =
      Array.isArray(menuBar) && menuBar.includes(String(accessKey));
    return result;
  };

  const checkPathMatch = (
    routePath?: string,
    currentPath?: string,
  ): boolean => {
    if (!routePath || !currentPath) return false;
    if (currentPath === routePath) return true;
    const pathWithSlash = routePath.endsWith("/") ? routePath : routePath + "/";
    return currentPath.startsWith(pathWithSlash);
  };

  const isItemActive = (item: NavItem, currentPath: string): boolean => {
    if (item.path && checkPathMatch(item.path, currentPath)) return true;
    if (
      item.relatedRoutes &&
      item.relatedRoutes.some((r) => checkPathMatch(r, currentPath))
    )
      return true;
    if (
      item.children &&
      item.children.some((child) => isItemActive(child, currentPath))
    )
      return true;
    return false;
  };

  const [openSubMenus, setOpenSubMenus] = useState<Record<string, boolean>>(
    () => {
      const activeSubmenus: Record<string, boolean> = {};
      const isHrmsActive =
        checkPathMatch(RoutesLink.superAdminDepartment, location.pathname) ||
        checkPathMatch(RoutesLink.superAdminBranch, location.pathname) ||
        checkPathMatch(RoutesLink.superAdminDesignation, location.pathname) ||
        checkPathMatch(RoutesLink.shiftAndTime, location.pathname) ||
        checkPathMatch(RoutesLink.superAdminSite, location.pathname) ||
        checkPathMatch(RoutesLink.superAdminCampus, location.pathname) ||
        checkPathMatch(RoutesLink.superAdminApplication, location.pathname) ||
        checkPathMatch(RoutesLink.salaryHead, location.pathname) ||
        checkPathMatch(RoutesLink.branchSalaryHead, location.pathname) ||
        checkPathMatch(RoutesLink.createSalary, location.pathname) ||
        checkPathMatch(RoutesLink.branchSalaryBackup, location.pathname) ||
        checkPathMatch(RoutesLink.offboardingList, location.pathname) ||
        checkPathMatch(RoutesLink.startOffboarding, location.pathname) ||
        checkPathMatch(RoutesLink.leaveSetting, location.pathname);
      const isEmpSalaryActive =
        checkPathMatch(RoutesLink.salaryHead, location.pathname) ||
        checkPathMatch(RoutesLink.branchSalaryHead, location.pathname) ||
        checkPathMatch(RoutesLink.createSalary, location.pathname) ||
        checkPathMatch(RoutesLink.branchSalaryBackup, location.pathname);
      const isLeavesActive =
        checkPathMatch(RoutesLink.myLeave, location.pathname) ||
        checkPathMatch(RoutesLink.teamLeave, location.pathname) ||
        checkPathMatch(RoutesLink.addLeave, location.pathname) ||
        checkPathMatch(RoutesLink.editLeave, location.pathname) ||
        checkPathMatch(RoutesLink.leaveDetail, location.pathname) ||
        checkPathMatch(RoutesLink.teamLeaveDetail, location.pathname);

      if (isHrmsActive) activeSubmenus["HRMS Settings"] = true;
      if (isEmpSalaryActive) activeSubmenus["Employee Salary"] = true;
      if (isLeavesActive) activeSubmenus["Leaves"] = true;
      return activeSubmenus;
    },
  );

  const toggleSubMenu = (label: string) => {
    setOpenSubMenus((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  const sidebarItems: NavItem[] = [
    {
      Icon: LayoutDashboard,
      label: "Dashboard",
      path: RoutesLink.superAdminDashboard,
      roles: [],
    },
    {
      Icon: User,
      label: "Onboard",
      path: RoutesLink.superAdminOnboard,
      roles: [],
      accessKey: permissionKeys?.onboard,
    },
    // attendance
    {
      Icon: Clock,
      label: "Attendance",
      roles: [],
      accessKey: permissionKeys.attendance,
      children: [
        {
          Icon: UserCheck,
          label: "My Attendance",
          path: RoutesLink.myAttendance,
          relatedRoutes: [RoutesLink.attendanceLog],
          accessKey: permissionKeys.attendance,
          roles: [],
        },

        ...(userDetails?.designation?.canAccessTeam ||
          Number(userDetails?.designation?.level) === 1 ||
          Number(userDetails?.designation?.level) === 0
          ? [
            {
              Icon: Users,
              label: "Daily Team Attendance",
              path: RoutesLink.dailyTeamAttendance,
              accessKey: permissionKeys.attendance,
              roles: [],
            },
          ]
          : []),

        ...(userDetails?.designation?.canAccessTeam ||
          Number(userDetails?.designation?.level) === 1 ||
          Number(userDetails?.designation?.level) === 0
          ? [
            {
              Icon: CalendarDays,
              label: "Monthly Team Att..",
              path: RoutesLink.monthlyTeamAttendance,
              accessKey: permissionKeys.attendance,
              roles: [],
            },
          ]
          : []),
        ...(
          Number(userDetails?.designation?.level) === 1 ||
            Number(userDetails?.designation?.level) === 0
            ? [
              {
                Icon: CalendarDays,
                label: "Manage Monthly Bulk Attendance",
                path: RoutesLink.manageMonthlyBulkAttendance,
                // accessKey: permissionKeys.attendance,
                roles: [],
              },
            ]
            : []),
      ],
    },
    // Leave
    {
      Icon: CalendarOff,
      label: "Leaves",
      roles: [],
      accessKey: permissionKeys.leave,
      children: [
        {
          Icon: User,
          label: "My Leave",
          path: RoutesLink.myLeave,
          accessKey: permissionKeys.leave,
          relatedRoutes: [RoutesLink.addLeave, RoutesLink.leaveDetail],
          roles: [],
        },
        ...(userDetails?.designation?.canAccessTeam
          ? [
            {
              Icon: Users,
              label: "Team Leave",
              path: RoutesLink.teamLeave,
              accessKey: permissionKeys?.leave,
              relatedRoutes: [RoutesLink.teamLeaveDetail],
              roles: [],
            },
          ]
          : []),
      ],
    },
    // WFH
    {
      Icon: Home,
      label: "WFH",
      accessKey: permissionKeys.wfh,
      roles: [],
      children: [
        {
          Icon: Laptop,
          label: "My WFH",
          path: RoutesLink.wfhRequest,
          accessKey: permissionKeys.wfh,
          relatedRoutes: [RoutesLink.addWfh, RoutesLink.wfhDetail],
          roles: [],
        },
        ...(userDetails?.designation?.canAccessTeam
          ? [
            {
              Icon: Monitor,
              label: "Team WFH",
              path: RoutesLink.teamWfh,
              accessKey: permissionKeys.wfh,
              relatedRoutes: [RoutesLink.teamWfhDetail],
              roles: [],
            },
          ]
          : []),
      ],
    },
    // OD
    {
      Icon: Briefcase,
      label: "OD",
      accessKey: permissionKeys.od,
      roles: [],
      children: [
        {
          Icon: MapPin,
          label: "My OD",
          path: RoutesLink.myOd,
          accessKey: permissionKeys.od,
          relatedRoutes: [RoutesLink.createOd, RoutesLink.odDetail],
          roles: [],
        },
        ...(userDetails?.designation?.canAccessTeam
          ? [
            {
              Icon: Navigation,
              label: "Team OD",
              path: RoutesLink.teamOd,
              accessKey: permissionKeys.od,
              relatedRoutes: [RoutesLink.teamOdDetail],
              roles: [],
            },
          ]
          : []),
      ],
    },

    //regularization

    {
      Icon: History,
      label: "Regularization",
      roles: [],
      accessKey: permissionKeys.regularization,
      children: [
        {
          Icon: FileCheck,
          label: "My regularization",
          path: RoutesLink.myRegularization,
          accessKey: permissionKeys.regularization,
          relatedRoutes: [
            RoutesLink.createRegularization,
            RoutesLink.regularizationDetail,
          ],
          roles: [],
        },
        ...(userDetails?.designation?.canAccessTeam
          ? [
            {
              Icon: Navigation,
              label: "Team regularization",
              path: RoutesLink.teamRegularization,
              accessKey: permissionKeys.regularization,
              relatedRoutes: [RoutesLink.regularizationDetail],
              roles: [],
            },
          ]
          : []),
      ],
    },

    {
      Icon: Receipt,
      label: "Payslip",
      roles: [],
      accessKey: permissionKeys.payslip,
      children: [
        {
          Icon: Receipt,
          label: "My Payslip",
          path: RoutesLink.payslip,
          relatedRoutes: [RoutesLink.payslipDetail],
          roles: [],
        },
        ...(userDetails?.designation?.canAccessTeam
          ? [
            {
              Icon: Users,
              label: "Team Payslip",
              path: RoutesLink.teamsPayslip,
              accessKey: permissionKeys.payslip,
              relatedRoutes: [RoutesLink.payslipDetail],
              roles: [],
            },
          ]
          : []),
      ],
    },

    // Create salary
    // {
    //     Icon: Receipt,
    //     label: "Create Salary",
    //     path: RoutesLink.createSalary,
    //     roles: [],
    //     accessKey: permissionKeys.hrmSetting,
    // },

    // Payslip
    // org

    {
      Icon: Network,
      label: "Org Structure",
      path: RoutesLink.superAdminOrganisation,
      accessKey: permissionKeys.organisation,
      roles: [],
    },
    {
      Icon: FileCheck,
      label: "Exit Clearance",
      path: RoutesLink.exitClearanceList,
      accessKey: permissionKeys.exitClearance,
      relatedRoutes: [
        RoutesLink.viewExitClearance,
        RoutesLink.exitClearanceList,
      ],
      roles: [],
    },

    ...(Number(userDetails?.designation?.level) === 0 ||
      Number(userDetails?.designation?.level) === 1
      ? [
        {
          Icon: MonitorCog,
          label: "HRMS Settings",
          accessKey: permissionKeys.hrmSetting,
          roles: [],
          children: [
            {
              Icon: UserPlus,
              label: "Employee",
              path: RoutesLink.superAdminEmplyoeeOnboard,
              accessKey: permissionKeys.hrmSetting,
              relatedRoutes: [
                RoutesLink.addEmplyoeeOnboard,
                RoutesLink.editEmplyoeeOnboard,
                // RoutesLink.employeeDetail,
              ],
              roles: [],
            },
            {
              Icon: UserMinus,
              label: "Emp. Offboard",
              path: RoutesLink.offboardingList,
              accessKey: permissionKeys.hrmSetting,
              relatedRoutes: [
                RoutesLink.startOffboarding,
                RoutesLink?.editOffboarding,
              ],
              roles: [],
            },
            {
              Icon: DollarSign,
              label: "Employees Salary's",
              accessKey: permissionKeys.hrmSetting,
              roles: [],
              children: [
                {
                  Icon: DollarSign,
                  label: "Salary Head",
                  accessKey: permissionKeys.hrmSetting,
                  path: RoutesLink.salaryHead,
                  relatedRoutes: [
                    RoutesLink?.addSalaryHead,
                    RoutesLink?.editSalaryHead,
                  ],
                  roles: [],
                },
                {
                  Icon: Receipt,
                  label: "Branch Salary Head",
                  accessKey: permissionKeys.hrmSetting,
                  path: RoutesLink.branchSalaryHead,
                  relatedRoutes: [
                    RoutesLink?.addBranchSalaryHead,
                    RoutesLink?.editBranchSalaryHead,
                  ],
                  roles: [],
                },
                {
                  Icon: Receipt,
                  label: "Create Salary",
                  path: RoutesLink.createSalary,
                  relatedRoutes: [RoutesLink.createSalary],
                  roles: [],
                  accessKey: permissionKeys.hrmSetting,
                },
                // {
                //     Icon: Badge,
                //     label: "Branch Salary Backups Config",
                //     accessKey: permissionKeys.hrmSetting,
                //     path: RoutesLink.branchSalaryBackup,
                //     relatedRoutes: [RoutesLink.branchSalaryBackup],
                //     roles: [],
                // },
              ],
            },
            {
              Icon: FileText,
              label: "Application",
              accessKey: permissionKeys.hrmSetting,
              path: RoutesLink.superAdminApplication,
              relatedRoutes: [
                RoutesLink?.addApplication,
                RoutesLink?.editApplication,
              ],
              roles: [],
            },
            {
              Icon: GitBranch,
              label: "Branch",
              accessKey: permissionKeys.hrmSetting,
              path: RoutesLink.superAdminBranch,
              relatedRoutes: [RoutesLink?.addBranch, RoutesLink?.editBranch],
              roles: [],
            },
            {
              Icon: MapPin,
              label: "Site",
              accessKey: permissionKeys.hrmSetting,
              path: RoutesLink.superAdminSite,
              relatedRoutes: [RoutesLink?.addSite, RoutesLink?.editSite],
              roles: [],
            },
            {
              Icon: GraduationCap,
              label: "Campus",
              accessKey: permissionKeys.hrmSetting,
              path: RoutesLink.superAdminCampus,
              relatedRoutes: [RoutesLink?.addCampus, RoutesLink?.editCampus],
              roles: [],
            },
            {
              Icon: Layers,
              label: "Department",
              accessKey: permissionKeys.hrmSetting,
              path: RoutesLink.superAdminDepartment,
              relatedRoutes: [
                RoutesLink?.addDepartment,
                RoutesLink?.editDepartment,
              ],
              roles: [],
            },
            {
              Icon: Award,
              label: "Designation",
              accessKey: permissionKeys.hrmSetting,
              path: RoutesLink.superAdminDesignation,
              relatedRoutes: [
                RoutesLink?.addDesignation,
                RoutesLink?.editDesignation,
              ],
              roles: [],
            },
            {
              Icon: Calendar,
              label: "Manage Holidays",
              accessKey: permissionKeys.hrmSetting,
              path: RoutesLink.superAdminHoliday,
              relatedRoutes: [
                RoutesLink?.addHoliday,
                RoutesLink?.editHoliday,
              ],
              roles: [],
            },
            {
              Icon: Clock,
              label: "Shift and Time",
              accessKey: permissionKeys.hrmSetting,
              path: RoutesLink.shiftAndTime,
              relatedRoutes: [
                RoutesLink?.addShiftAndTime,
                RoutesLink?.editShiftAndTime,
                RoutesLink?.viewShiftAndTime,
              ],
              roles: [],
            },

            {
              Icon: CalendarRange,
              label: "Leave Setting",
              accessKey: permissionKeys.hrmSetting,
              path: RoutesLink.leaveSetting,
              relatedRoutes: [
                RoutesLink?.addLeaveSetting,
                RoutesLink?.editLeaveSetting,
              ],
              roles: [],
            },
            {
              Icon: UserPlus,
              label: "Company Documents",
              path: RoutesLink.companyDocument,
              accessKey: permissionKeys.hrmSetting,
              relatedRoutes: [
                RoutesLink.createCompanyDocument,
                RoutesLink.editCompanyDocument,
                RoutesLink.companyDocumentDetail,
              ],
              roles: [],
            },
            // {
            //   Icon: LocationEdit,
            //   label: "Transfer",
            //   path: RoutesLink.transfer,
            //   // accessKey: permissionKeys.hrmSetting,
            //   // relatedRoutes: [RoutesLink.createCompanyDocument, RoutesLink.editCompanyDocument, RoutesLink.companyDocumentDetail],
            //   roles: [],
            // },
          ],
        },
      ]
      : []),

    // Holiday List
    {
      Icon: Calendar,
      label: "Holidays",
      path: RoutesLink.holidayList,
      roles: [],
    },

    // Notifications
    {
      Icon: Bell,
      label: "Notifications",
      // accessKey: permissionKeys.hrmSetting,
      path: RoutesLink.notification,
      roles: [],
    },
    {
      Icon: FileText,
      label: "Documents",
      // accessKey: permissionKeys.hrmSetting,
      path: RoutesLink.documentSetting,
      roles: [],
    },
  ];

  React.useEffect(() => {
    setOpenSubMenus((prev) => {
      const next = { ...prev };
      let changed = false;
      const checkAndOpen = (items: NavItem[]) => {
        items?.forEach((item) => {
          if (item.children) {
            const isActive = isItemActive(item, location.pathname);
            if (isActive && !next[item.label]) {
              next[item.label] = true;
              changed = true;
            }
            checkAndOpen(item.children);
          }
        });
      };
      checkAndOpen(sidebarItems);
      return changed ? next : prev;
    });
  }, [location.pathname]);

  if (
    !userDetails &&
    (userDetailsLoading === "pending" || userDetailsLoading === "idle")
  ) {
    return (
      <aside
        className={`
                fixed inset-y-0 left-0 z-50 h-screen flex flex-col transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]
                bg-[#0B1121] border-r border-white/10 shadow-2xl overflow-hidden
                ${isCollapsed ? "w-20" : "w-72"}
                ${isMobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
            `}
      >
        <div className="flex items-center justify-center h-full">
          <Loader2 className="text-primary animate-spin" size={24} />
        </div>
      </aside>
    );
  }

  return (
    <TooltipProvider>
      {/* Mobile Backdrop */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden backdrop-blur-sm transition-opacity duration-300"
          onClick={toggleMobileSidebar}
        />
      )}

      <aside
        className={`
                fixed inset-y-0 left-0 z-50 h-screen flex flex-col transition-all duration-100 ease-[cubic-bezier(0.16,1,0.3,1)]
                bg-[#0B1121] border-r border-white/10 shadow-2xl overflow-hidden
                ${isCollapsed ? "w-20" : "w-72"}
                ${isMobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
            `}
      >
        {/* Subtle background glow for depth */}
        <div className="absolute top-0 left-0 w-full h-full bg-linear-to-b from-primary/5 to-transparent pointer-events-none -z-10" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-50 h-50 bg-primary/20 blur-[100px] rounded-full pointer-events-none -z-10" />

        {/* Header */}
        <div className="p-4 border-b border-white/5 relative z-10 flex items-center justify-start">
          <div className="flex items-center gap-3.5">
            <div className="relative shrink-0 flex items-center justify-center w-11 h-11 rounded-xl bg-primary">
              <HeartPulse
                className="text-white relative z-10"
                size={24}
                strokeWidth={2.5}
              />
            </div>
            {!isCollapsed && (
              <div className="flex flex-col">
                <span className="font-display text-[16px] font-bold text-white tracking-wide leading-tight shadow-sm">
                  SSSST HRMS
                </span>
                <span className="text-[11px] font-medium text-primary/80 uppercase tracking-widest mt-0.5">
                  SMT/NGO/2008
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav
          ref={navRef}
          onScroll={(e) => {
            globalSidebarScrollTop = e.currentTarget.scrollTop;
          }}
          className="flex-1 overflow-y-auto p-4 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent"
        >
          <div className="space-y-1.5">
            {sidebarItems?.map((item) => {
              const { Icon } = item;

              const isAccess = checkAccess(item?.accessKey);
              if (!isAccess) return null;

              if (item.children) {
                const isChildActive = isItemActive(item, location.pathname);

                return (
                  <div key={item.label} className="space-y-1">
                    <SidebarItemTooltip
                      label={
                        isTandcAccepted ? item.label : `${item.label} (Locked)`
                      }
                      enabled={isCollapsed}
                    >
                      <button
                        onClick={() => {
                          if (!isTandcAccepted) {
                            showToastNotification(
                              "danger",
                              "Please accept the Terms & Conditions to continue.",
                            );
                            dispatch(openTermsModal());
                            return;
                          }
                          if (isCollapsed) {
                            toggleSidebar();
                            if (!openSubMenus[item.label]) {
                              toggleSubMenu(item.label);
                            }
                          } else {
                            toggleSubMenu(item.label);
                          }
                        }}
                        className={`
                                                    w-full group relative overflow-hidden flex items-center ${isCollapsed ? "justify-center px-2" : "justify-between px-4"} py-3 text-[14px] font-medium rounded-xl transition-all duration-300 cursor-pointer
                                                    ${isChildActive
                            ? "bg-primary/10 text-white border border-primary/10"
                            : "text-slate-400 border border-transparent hover:text-white hover:bg-primary/10 hover:border-primary/10"
                          } 
                                                `}
                      >
                        <div
                          className={`flex items-center ${isCollapsed ? "justify-center w-full" : "gap-3.5"} relative z-10`}
                        >
                          <div className="flex items-center justify-center">
                            <Icon
                              size={20}
                              className={`transition-all duration-300 ease-out
                                                                ${isChildActive
                                  ? "text-primary scale-110"
                                  : "group-hover:text-white group-hover:scale-110"
                                }
                                                            `}
                              strokeWidth={isChildActive ? 2.5 : 2}
                            />
                          </div>
                          {!isCollapsed && (
                            <span className="tracking-wide transition-transform duration-300 group-hover:translate-x-1">
                              {item.label}
                            </span>
                          )}
                          {!isTandcAccepted && !isCollapsed && <LockedBadge />}
                        </div>

                        {!isCollapsed && (
                          <ChevronDown
                            size={16}
                            className={`text-slate-400 transition-transform duration-350 relative z-10
                                                            ${openSubMenus[item.label] ? "rotate-180 text-primary" : "group-hover:text-white"}
                                                        `}
                          />
                        )}
                      </button>
                    </SidebarItemTooltip>

                    {openSubMenus[item.label] && !isCollapsed && (
                      <div className="pl-6 space-y-1 mt-1 transition-all duration-300 ease-in-out">
                        {item?.children?.map((child) => {
                          if (!checkAccess(child?.accessKey)) return null;

                          if (child.children) {
                            const SubIcon = child.Icon;
                            const isSubChildActive = isItemActive(
                              child,
                              location.pathname,
                            );

                            return (
                              <div key={child.label} className="space-y-1">
                                <button
                                  onClick={() => {
                                    if (!isTandcAccepted) {
                                      showToastNotification(
                                        "danger",
                                        "Please accept the Terms & Conditions to continue.",
                                      );
                                      dispatch(openTermsModal());
                                      return;
                                    }
                                    toggleSubMenu(child.label);
                                  }}
                                  className={`
                                                                        w-full group relative overflow-hidden flex items-center justify-between px-3.5 py-2 text-[13px] font-medium rounded-lg transition-all duration-300 cursor-pointer
                                                                        ${isSubChildActive
                                      ? "bg-primary/10 text-white border border-primary/10"
                                      : "text-slate-400 border border-transparent hover:text-white hover:bg-primary/10 hover:border-white/5"
                                    }
                                                                    `}
                                >
                                  <div className="flex items-center gap-3 relative z-10">
                                    <div className="flex items-center justify-center">
                                      <SubIcon
                                        size={16}
                                        className={`transition-all duration-300 ease-out
                                                                                    ${isSubChildActive
                                            ? "text-primary scale-110"
                                            : "group-hover:text-white group-hover:scale-110"
                                          }
                                                                                `}
                                        strokeWidth={isSubChildActive ? 2.5 : 2}
                                      />
                                    </div>
                                    <span className="tracking-wide transition-transform duration-300 group-hover:translate-x-1">
                                      {child.label}
                                    </span>
                                    {!isTandcAccepted && <LockedBadge />}
                                  </div>
                                  <ChevronDown
                                    size={14}
                                    className={`text-slate-400 transition-transform duration-350 relative z-10
                                                                            ${openSubMenus[child.label] ? "rotate-180 text-primary" : "group-hover:text-white"}
                                                                        `}
                                  />
                                </button>

                                {openSubMenus[child.label] && (
                                  <div className="pl-3.5 space-y-1 mt-1 transition-all duration-300 ease-in-out border-l border-white/10 ml-2">
                                    {child.children.map((nestedChild) => {
                                      if (!checkAccess(nestedChild.accessKey))
                                        return null;
                                      const NestedIcon = nestedChild.Icon;
                                      const isNestedActive =
                                        (nestedChild.path &&
                                          checkPathMatch(
                                            nestedChild.path,
                                            location.pathname,
                                          )) ||
                                        (nestedChild.relatedRoutes &&
                                          nestedChild.relatedRoutes.some((r) =>
                                            checkPathMatch(
                                              r,
                                              location.pathname,
                                            ),
                                          ));

                                      return (
                                        <Link
                                          key={
                                            nestedChild.path ||
                                            nestedChild.label
                                          }
                                          to={nestedChild.path || "#"}
                                          onClick={handleGuardedNavClick}
                                          className={`
                                                                                        group relative overflow-hidden flex items-center gap-2.5 px-3 py-2 text-[12.5px] font-medium rounded-lg transition-all duration-300
                                                                                        ${isNestedActive
                                              ? "bg-white/5 text-white border border-white/10 shadow-[0_2px_10px_rgba(0,0,0,0.1)]"
                                              : "text-slate-400 border border-transparent hover:text-white hover:bg-primary/10 hover:border-white/5"
                                            }
                                                                                    `}
                                        >
                                          {isNestedActive && (
                                            <div className="absolute inset-y-0 left-0 w-1 bg-primary rounded-r" />
                                          )}

                                          <div className="relative z-10 flex items-center justify-center">
                                            <NestedIcon
                                              size={15}
                                              className={`transition-all duration-300 ease-out
                                                                                                ${isNestedActive
                                                  ? "text-primary scale-110"
                                                  : "group-hover:text-white group-hover:scale-110"
                                                }
                                                                                            `}
                                              strokeWidth={
                                                isNestedActive ? 2.5 : 2
                                              }
                                            />
                                          </div>

                                          <span className="tracking-wide relative z-10 transition-transform duration-300 group-hover:translate-x-1">
                                            {nestedChild.label}
                                          </span>

                                          {!isTandcAccepted && (
                                            <span className="ml-auto shrink-0 text-[10px] font-semibold text-amber-400/90 bg-amber-500/10 px-1.5 py-0.5 rounded-full border border-amber-500/20 relative z-10">
                                              Locked
                                            </span>
                                          )}
                                        </Link>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          }

                          const ChildIcon = child.Icon;
                          const isChildItemActive =
                            (child.path &&
                              checkPathMatch(child.path, location.pathname)) ||
                            (child.relatedRoutes &&
                              child.relatedRoutes.some((r) =>
                                checkPathMatch(r, location.pathname),
                              ));

                          return (
                            <Link
                              key={child.path}
                              to={child.path || "#"}
                              onClick={handleGuardedNavClick}
                              className={`
                                                                group relative overflow-hidden flex items-center gap-3 px-4 py-2 text-[13px] font-medium rounded-lg transition-all duration-300
                                                                ${isChildItemActive
                                  ? "bg-white/5 text-white border border-white/10 shadow-[0_2px_10px_rgba(0,0,0,0.1)]"
                                  : "text-slate-400 border border-transparent hover:text-white hover:bg-primary/10 hover:border-white/5"
                                }
                                                            `}
                            >
                              {isChildItemActive && (
                                <div className="absolute inset-y-0 left-0 w-1 bg-primary rounded-r" />
                              )}

                              <div className="relative z-10 flex items-center justify-center">
                                <ChildIcon
                                  size={16}
                                  className={`transition-all duration-300 ease-out
                                                                        ${isChildItemActive
                                      ? "text-primary scale-110"
                                      : "group-hover:text-white group-hover:scale-110"
                                    }
                                                                    `}
                                  strokeWidth={isChildItemActive ? 2.5 : 2}
                                />
                              </div>

                              <span className="tracking-wide relative z-10 transition-transform duration-300 group-hover:translate-x-1">
                                {child.label}
                              </span>

                              {!isTandcAccepted && (
                                <span className="ml-auto shrink-0 text-[10px] font-semibold text-amber-400/90 bg-amber-500/10 px-1.5 py-0.5 rounded-full border border-amber-500/20 relative z-10">
                                  Locked
                                </span>
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              const isActive =
                (item.path && checkPathMatch(item.path, location.pathname)) ||
                (item.relatedRoutes &&
                  item.relatedRoutes.some((r) =>
                    checkPathMatch(r, location.pathname),
                  ));

              return (
                <SidebarItemTooltip
                  key={item.path}
                  label={
                    isTandcAccepted ? item.label : `${item.label} (Locked)`
                  }
                  enabled={isCollapsed}
                >
                  <Link
                    to={item.path || "#"}
                    onClick={handleGuardedNavClick}
                    className={`
                                            group relative overflow-hidden flex items-center ${isCollapsed ? "justify-center px-2" : "gap-3.5 px-4"} py-3 text-[14px] font-medium rounded-xl transition-all duration-300
                                            ${isActive
                        ? "bg-primary/10 text-white border border-primary/10"
                        : "text-slate-400 border border-transparent hover:bg-primary/10 hover:border-primary/10"
                      } 
                                        `}
                  >
                    {isActive && (
                      <div className="absolute inset-0 bg-linear-to-r from-primary/10 to-transparent pointer-events-none" />
                    )}

                    <div
                      className={`relative z-10 flex items-center ${isCollapsed ? "justify-center w-full" : ""}`}
                    >
                      <Icon
                        size={20}
                        className={`transition-all duration-300 ease-out
                                                    ${isActive
                            ? "text-primary"
                            : ""
                          }
                                                `}
                        strokeWidth={isActive ? 2.5 : 2}
                      />
                    </div>

                    {!isCollapsed && (
                      <span className="tracking-wide relative z-10 transition-transform duration-300 group-hover:translate-x-1">
                        {item.label}
                      </span>
                    )}

                    {!isTandcAccepted && !isCollapsed && (
                      <span className="ml-auto shrink-0 text-[10px] font-semibold text-amber-400/90 bg-amber-500/10 px-1.5 py-0.5 rounded-full border border-amber-500/20 relative z-10">
                        Locked
                      </span>
                    )}

                    {isActive && !isCollapsed && isTandcAccepted && (
                      <div className="absolute right-4 w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                    )}
                  </Link>
                </SidebarItemTooltip>
              );
            })}
          </div>
        </nav>
      </aside>
    </TooltipProvider>
  );
};

export default Sidebar;